import { DualAxes, Line } from "@ant-design/charts";
import {
  BellOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  SearchOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  Empty,
  Form,
  Input,
  message,
  Row,
  Segmented,
  Table,
  Typography,
} from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import PrimaryButton from "../../components/Buttons/PrimaryButton";
import { parseDateToLocal } from "../../helpers/ParserHelper";
import { TableHelper } from "../../helpers/TableHelper";
import { get_language, translate } from "../../locale";
import DashServices from "../../services/DashServices";
import SelfMeasureServices from "../../services/SelfMeasuerServices";
import UserServices from "../../services/UserServices";
import CustomDatePicker from "../Datepicker/CustomDatePicker";
import SectionLoader from "../Loaders/SectionLoader";
import RangeForm from "./RangeForm";

const { Title } = Typography;

const WeightCharts = () => {
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selectedGraph, setSelectedGraph] = useState("graph");
  const [dateFrom, setDateFrom] = useState(
    new Date(moment().subtract(7, "days"))
  );
  const [dateTo, setDateTo] = useState(new Date());
  const { username } = useParams();
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
      total: 0,
    },
  });
  const [total, setTotal] = useState(0);
  const units = {
    // weight
    kg: "kg.",
    lb: "lb.",

    // diabetes
    mgdl: "mg/dl",

    // hypertension
    mmhg: "mg/hg",
    bpm: "bpm",
  };

  const [configGraph, setConfigGraph] = useState({
    data: [{ year: moment().format("DD/MM/YYYY HH:mm"), value: 0 }],
    height: 500,
    width: "100%",
    xField: "year",
    yField: "value",

    yAxis: {
      max: 300,
      min: 0,
      tickInterval: 50,
      title: {
        text: "Peso (kg.)",
      },
    },
    geometryOptions: [
      {
        geometry: "line",
        color: "#5B8FF9",
      },
      {
        geometry: "line",
        color: "#5AD8A6",
      },
    ],
    point: {
      size: 5,
      shape: "diamond | circule",
    },
    legend: {
      layout: "horizontal",
      position: "bottom",
      offsetY: 0,
    },
  });

  const [configDaily] = useState({
    data: [
      { day: "20/10/2022 15:04", "Glucemia (mg/dL)": 0, HbA1c: 0 },
      { day: "20/10/2022 15:04", "Glucemia (mg/dL)": 0, HbA1c: 0 },
    ],
    height: 500,
    width: "100%",
    xField: "day",
    yField: ["Glucemia (mg/dL)", "HbA1c"],
    yAxis: {
      "Glucemia (mg/dL)": {
        max: 120,
        min: 0,
        tickInterval: 20,
        title: {
          text: "Glucemia (mg/dL)",
        },
      },
      HbA1c: {
        max: 1.2,
        min: 0,
        tickInterval: 20,
        title: {
          text: "HbA1c",
        },
      },
    },
    xAxis: {
      grid: {
        line: {
          style: {
            stroke: "black",
            lineWidth: 1,
            strokeOpacity: 0.2,
            cursor: "pointer",
          },
        },
      },
    },
    geometryOptions: [
      {
        geometry: "line",
        color: "#5B8FF9",
      },
      {
        geometry: "line",
        color: "#5AD8A6",
      },
    ],
    point: {
      size: 5,
      shape: "diamond | circule",
    },
    legend: {
      layout: "horizontal",
      position: "bottom",
      offsetY: 10,
    },
  });

  const [dataList, setDataList] = useState([]);

  const columnsList = [
    {
      title: "Fecha",
      dataIndex: "measure_date",
      key: "measure_date",
      render: (_, record) => moment(record.measure_date).format("DD/MM/YYYY"),
      sorter: { multiple: 1 },
    },
    {
      title: "Hora",
      dataIndex: "measure_time",
      key: "measure_time",
      sorter: { multiple: 2 },
    },
    {
      title: "Modo",
      dataIndex: "mode",
      key: "mode",
      render: (_, record) =>
        record.mode === "automatic" ? "Automático" : "Manual",
      sorter: { multiple: 3 },
    },
    {
      title: "Peso",
      dataIndex: "value",
      key: "value",
      render: (_, record) => {
        return record.peso ? `${record.peso} ${units[record.unit]}` : "No data";
      },
      sorter: { multiple: 4 },
    },
    {
      title: "IMC",
      dataIndex: "bmi",
      key: "bmi",
      sorter: { multiple: 5 },
    },
  ];

  useEffect(() => {
    loadGraph();
    loadTotalGraph();
  }, [JSON.stringify(tableParams)]);

  const loadGraph = async () => {
    setLoading(true);
    try {
      let pagination = {
        page_size: tableParams.pagination.pageSize,
        page_num: tableParams.pagination.current,
      };
      const sorter = TableHelper.sorter(tableParams);
      if (sorter !== null) {
        pagination = { ...pagination, order_by: sorter };
      }
      const res = await SelfMeasureServices.getControlHistory(
        moment(dateFrom).format("YYYY-MM-DD"),
        moment(dateTo).format("YYYY-MM-DD"),
        "weight",
        username,
        pagination
      );
      if (res.status >= 200 && res.status < 300) {
        const list = res.data.controls.map((i) => {
          const value = i.answers.findLast((i) => i.peso !== undefined);
          return {
            measure_date: i.measure_date,
            measure_time: i.measure_time,
            mode: i.mode,
            peso: value.peso,
            unit: value.unit,
            bmi: value.bmi,
          };
        });
        setDataList(list);
        setTotal(res.data.total_pages * tableParams.pagination.pageSize);
      }

      setLoading(false);
      setSearching(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
      setSearching(false);
    }
  };

  // Load ALL data in the graph
  const loadTotalGraph = async () => {
    setLoading(true);
    try {
      let pagination = {
        page_size: 1000,
        page_num: 1,
      };
      const sorter = TableHelper.sorter(tableParams);
      if (sorter !== null) {
        pagination = { ...pagination, order_by: sorter };
      }
      const res = await SelfMeasureServices.getControlHistory(
        moment(dateFrom).format("YYYY-MM-DD"),
        moment(dateTo).format("YYYY-MM-DD"),
        "weight",
        username,
        pagination
      );
      if (res.status >= 200 && res.status < 300) {
        const data = res.data.controls.map((i) => ({
          value: Number.parseFloat(
            i.answers.findLast((i) => i.peso !== undefined)?.peso
          ),
          year: parseDateToLocal(`${i.measure_date} ${i.measure_time}`),
        }));
        setConfigGraph((v) => ({ ...v, data: data.reverse() }));
      }

      setLoading(false);
      setSearching(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
      setSearching(false);
    }
  };

  const handleChangeItem = (value) => {
    setSelectedGraph(value);
    switch (value) {
      case "graph":
        loadTotalGraph();
        loadGraph();
        break;
      case "list":
        loadGraph();
        break;
    }
  };
  const onChangeDatePicker = (name, value) => {
    if (name === "dateFrom") {
      setDateFrom(value);
    } else if (name === "dateTo") {
      setDateTo(value);
    }
  };
  const handleSearch = (e) => {
    setSearching(true);
    loadTotalGraph();
    loadGraph();
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      sorter,
    });

    // `dataSource` is useless since `pageSize` changed
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setDataList([]);
      setConfigGraph([]);
    }
  };

  return (
    <>
      {selectedGraph !== "range" && (
        <Form onFinish={handleSearch}>
          <Row className="mt-6 mb-14 relative z-10" gutter={[40, 10]}>
            <Col xs={{ span: 24 }} md={{ span: 8 }} lg={{ span: 6 }}>
              <div className="flex flex-row flex-no-wrap items-stretch justify-end md:justify-start">
                <div className="flex flex-col justify-center mr-4 md:mr-0 lg:mr-4 text-sm md:text-xs lg:text-sm">
                  {translate("Fecha desde")}
                </div>
                <CustomDatePicker
                  locale={get_language}
                  selected={dateFrom}
                  onChange={(e) => onChangeDatePicker("dateFrom", e)}
                />
              </div>
            </Col>
            <Col xs={{ span: 24 }} md={{ span: 8 }} lg={{ span: 6 }}>
              <div className="flex flex-row flex-no-wrap items-stretch justify-end md:justify-start">
                <div className="flex flex-col justify-center mr-4 md:mr-0 lg:mr-4 text-sm md:text-xs lg:text-sm">
                  {translate("Fecha hasta")}
                </div>
                <CustomDatePicker
                  locale={get_language}
                  selected={dateTo}
                  onChange={(e) => onChangeDatePicker("dateTo", e)}
                />
              </div>
            </Col>
            <Col
              xs={{ span: 24 }}
              md={{ span: 6 }}
              className="text-right md:text-left"
            >
              <Button
                htmlType="submit"
                type="primary"
                className="bg-custom_primary"
                icon={
                  searching ? (
                    <LoadingOutlined />
                  ) : (
                    <SearchOutlined style={{ position: "relative" }} />
                  )
                }
              >
                {translate("Buscar")}
              </Button>
            </Col>
          </Row>
        </Form>
      )}
      <Row className="mb-14">
        <Col>
          <Segmented
            options={[
              { value: "graph", label: "Gráfico" },
              // { value: "daily_pattern", label: "Patrón diario" },
              // { value: "weekly_pattern", label: "Patrón semanal" },
              // { value: "scattering", label: "Dispersión" },
              { value: "list", label: "Listado" },
              { value: "range", label: "Rangos" },
            ]}
            onChange={handleChangeItem}
          />
        </Col>
      </Row>
      <Row className="relative mb-14">
        {loading && <SectionLoader />}

        {selectedGraph === "graph" && (
          <Col span={24}>
            <Line {...configGraph} />
          </Col>
        )}
        {selectedGraph === "daily_pattern" && (
          <Col span={24}>
            <DualAxes {...configDaily} />
          </Col>
        )}
        {selectedGraph === "list" && (
          <Col span={24} className="overflow-x-scroll md:overflow-x-visible">
            <Table
              rowKey={(i) => `${i.measure_date} ${i.measure_time}`}
              columns={columnsList}
              dataSource={dataList}
              locale={{
                emptyText: <Empty description={translate("No hay datos")} />,
                sortTitle: translate("Ordenar"),
                triggerDesc: translate("Ordenar (descendente)"),
                triggerAsc: translate("Ordenar (ascendente)"),
                cancelSort: translate("Cancelar orden"),
              }}
              pagination={{ ...tableParams.pagination, total: total }}
              onChange={handleTableChange}
              scroll={{ x: 300 }}
            />
          </Col>
        )}
        {selectedGraph === "range" && (
          <Col span={24}>
            <Row gutter={[48, 48]}>
              <Col xs={{ span: 24 }} md={{ span: 12 }}>
                <RangeForm
                  initialValues={{
                    patient: username,
                    type: "peso",
                    max_value_alert: 120,
                    max_value: 90,
                    min_value: 60,
                    min_value_alert: 50,
                  }}
                  title={translate("Rango de peso")}
                />
              </Col>
              <Col xs={{ span: 24 }} md={{ span: 12 }}>
                <RangeForm
                  initialValues={{
                    patient: username,
                    type: "diff-peso",
                    max_value_alert: 0,
                    max_value: 0,
                  }}
                  title={translate("Aumento de peso")}
                  onlyMax={true}
                />
              </Col>

              <Col xs={{ span: 24 }} md={{ span: 12 }}>
                <RangeForm
                  initialValues={{
                    patient: username,
                    type: "register_weight",
                    max_value_alert: 30,
                    max_value: 7,
                  }}
                  title={translate("Días sin registros de peso")}
                  onlyMax={true}
                />
              </Col>
            </Row>
          </Col>
        )}
      </Row>
    </>
  );
};

const WeightGainRangeForm = () => {
  const [loading, setLoading] = useState(false);
  const { username } = useParams();
  const [values, setValues] = useState({
    patient: username,
    type: "diff-peso",
    max_value_alert: 0,
    max_value: 0,
  });
  const [checkboxValues, setCheckboxValues] = useState({
    patient: username,
    type: "diff-peso",
    max_value_alert: false,
    max_value: false,
  });
  const [error, setError] = useState("");

  useEffect(() => {
    getRanges();
  }, []);

  const getRanges = async () => {
    try {
      setLoading(true);
      const r = await UserServices.getPatientRange(username);
      if (r.status === 200) {
        const ranges = r.data.ranges.findLast((i) => i.type === "diff-peso");
        if (ranges.type) {
          setValues((v) => ({
            ...v,
            ...ranges,
          }));
          setCheckboxValues((v) => ({
            ...v,
            max_value_alert: ranges.max_value_alert_check,
            max_value: ranges.max_value_check,
          }));
        }
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  const handleChange = (e) => {
    let { name, value } = e.target;
    value = value == "" ? "0" : value;
    setValues((v) => ({ ...v, [name]: value }));
    if (name === "max_value_alert") {
      setCheckboxValues((v) => ({ ...v, ["max_value_alert"]: value != 0 }));
    } else if (name === "max_value") {
      setCheckboxValues((v) => ({ ...v, ["max_value"]: value != 0 }));
    }
  };

  const handleSaveRange = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await DashServices.updateRange(values);
      const resCheck = await DashServices.updateCheckboxRange(checkboxValues);

      if (
        (checkboxValues.max_value_alert === true &&
          values.max_value_alert == 0) ||
        (checkboxValues.max_value === true && values.max_value == 0)
      ) {
        throw new TypeError(
          "Para activar las notificaciones el valor debe ser mayor a cero (0)"
        );
      }

      if (res.data.message === "ok" && resCheck.data.message === "ok") {
        message.success("Los rangos han sido actualizados exitosamente.");
      } else {
        setError(res.data.message);
      }
      setLoading(false);
    } catch (error) {
      console.log(error.message);
      if (error.message === undefined) {
        setError(error);
      } else {
        setError(error.message);
      }
      setLoading(false);
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setCheckboxValues((v) => ({ ...v, [name]: checked }));
  };
  return (
    <Row className="border border-solid border-custom_gray">
      <Col span={24} className="py-2 px-6 bg-custom_lightgray">
        <Title level={4}>{translate("Aumento de peso")}</Title>
      </Col>
      <Col className="p-6">
        <Form onFinish={handleSaveRange}>
          <Row gutter={32}>
            <Col>
              <Row justify="start" className="mb-2">
                <Col className="flex items-center bg-custom_darkred rounded-tl rounded-bl py-4 px-8 w-32">
                  <BellOutlined className="text-4xl text-white mr-2 leading-3" />
                  <Checkbox
                    name="max_value_alert"
                    onChange={handleCheckboxChange}
                    checked={checkboxValues.max_value_alert}
                  />
                </Col>
                <Col className="flex items-center bg-custom_red rounded-tr rounded-br py-4 px-8 w-32">
                  <ExclamationCircleOutlined className="text-4xl text-white ml-4 leading-3" />
                </Col>
              </Row>
              <Row justify="start">
                <Col className="flex items-center bg-custom_darkyellow rounded-tl rounded-bl py-4 px-8 w-32">
                  <BellOutlined className="text-4xl text-white mr-2 leading-3" />
                  <Checkbox
                    name="max_value"
                    onChange={handleCheckboxChange}
                    checked={checkboxValues.max_value}
                  />
                </Col>
                <Col className="flex items-center bg-custom_yellow rounded-tr rounded-br py-4 px-8 w-32">
                  <WarningOutlined className="text-4xl text-white ml-4 leading-3" />
                </Col>
              </Row>
            </Col>
            <Col className="flex flex-col justify-center gap-y-11">
              <Input
                type="number"
                name="max_value_alert"
                min={0}
                value={values.max_value_alert}
                onChange={handleChange}
              />
              <Input
                type="number"
                name="max_value"
                min={0}
                value={values.max_value}
                onChange={handleChange}
              />
            </Col>
            {error && (
              <Col span="24" className="text-center text-custom_red mt-2">
                {error}
              </Col>
            )}
            <Col span="24" className="text-center mt-4">
              <PrimaryButton htmlType="submit" loading={loading}>
                {translate("Guardar")}
              </PrimaryButton>
            </Col>
          </Row>
        </Form>
      </Col>
    </Row>
  );
};

export default WeightCharts;
